/**
 * Patch Pouch Customizer — Product Page Add-on
 * ----------------------------------------------
 * Pairs with sections/patch-customizer.liquid. The section only renders
 * when: (1) the current product uses the "pouch" product template, and
 * (2) the product carries a patch:N tag (e.g. patch:1, patch:2, patch:3,
 * patch:5) — that tag determines the max spot count below, no collection
 * setup required beyond picking the patch collection in section settings.
 *
 * Patch size/position is a shared DEFAULT per max spot count (see
 * SLOT_SIZE_BY_MAX / ROW_TOP below) — every product with the same patch:N
 * tag looks the same out of the box. If one specific product's photo
 * needs a different size or vertical position (different crop, different
 * bag shape, etc.), add either or both of these tags to JUST that
 * product — no code changes, no effect on any other product:
 *   patch-size:22     (patch box width, as a % of the photo — bigger
 *                       number = bigger patches)
 *   patch-top:50       (vertical center of the patch row, as a % down
 *                       the photo — smaller number = higher up)
 * Decimals are fine (e.g. patch-top:52.5).
 *
 * Layout model (dynamic reflow):
 *  - Patches are placed in ORDER, not into fixed named spots. Shoppers can
 *    add anywhere from 1 up to spotCount patches — they are not required
 *    to fill every spot (a Mini pouch shopper might only want "R", not
 *    "PR"). The same patch can be clicked multiple times (e.g. "RSRSS"
 *    reuses R and S).
 *  - Every time a patch is added or removed, ALL currently-placed patches
 *    re-flow into a horizontally-centered row sized for the new count —
 *    this is the "bump" effect: going from 1 patch to 2 recenters and
 *    shifts the first patch to make room, exactly like real patch-pouch
 *    product photography where more letters means a wider, more
 *    compressed monogram row. CSS transitions on the row items animate
 *    this smoothly (see .patch-customizer__patch-row-item in the CSS).
 *  - The numbered 1..spotCount indicator strip is a SEPARATE UI element
 *    positioned above the pouch photo — it is a progress readout / quick
 *    remove control, not a set of click targets overlaid on the fabric.
 *    It never determines where a patch visually renders.
 *
 * Data flow:
 *  - The Liquid section renders one "pouch" object (the current product,
 *    including its spotCount derived from its tag) and a "patches" array
 *    (real Shopify products from your Patches collection, with live
 *    price/inventory). Liquid's `| json` filter on a Money value (like
 *    variant.price) serializes the underlying integer CENTS, not a
 *    decimal dollar amount — so pouch.price and patch.price below are
 *    always integer cents. Keep all math in cents to avoid floating
 *    point/rounding bugs; only divide by 100 at the very end for display.
 *  - On Add to Cart, we POST multiple line items to Shopify's
 *    /cart/add.js in one request: the pouch variant, plus one line item
 *    per placed patch. Each patch line item carries line item properties:
 *    "Placement" (Patch 1, Patch 2, ... reflecting order placed), "Pouch",
 *    and a hidden "_customizer_id" tying every line item in that build
 *    back together. Because these are real product variants, Shopify
 *    handles pricing (pouch price + each patch's price) and inventory
 *    deduction natively — no custom price math needed.
 *  - The POUCH line item also carries "Patches to Apply (in order)" — a
 *    single readable summary of every patch in placement order (e.g.
 *    "1) Sea Turtle Patch,  2) B - Letter Patch"). Shopify shows all line
 *    item properties on the admin order page and packing slip already, so
 *    this turns the pouch's own line into a self-contained build sheet —
 *    staff don't have to cross-reference separate patch line items to
 *    know what to sew on.
 *
 * Patch groups (Patches for Customization / Letter Patches):
 *  - The "Choose your patches" picker is split into two groups, one per
 *    patch collection configured in the section settings
 *    (decorative_patch_collection, letter_patch_collection in the Liquid).
 *    Each patch product carries a "group" field ("decorative" or "letter")
 *    set server-side from which collection it came from. Rather than
 *    showing both at once, shoppers switch between them with two toggle
 *    tabs — "Patches for Customization" (decorative, active by default)
 *    and "Letter Patches" — so only one grid is visible at a time and
 *    letters never appear mixed in among decorative designs. The letter
 *    collection is sorted alphabetically at the collection level, so its
 *    grid always renders A→Z.
 *  - Both groups' grids are built up front (see renderPatchGrid), but only
 *    the active group's wrapper is visible — the other is hidden via the
 *    `hidden` attribute (see setActiveGroup). This keeps switching tabs
 *    instant with no re-render, and preserves each group's own scroll/View
 *    More state while it's hidden.
 *  - Each group's grid is capped at 3 visible rows by default (see
 *    applyGridCollapse below), with its OWN "View More" button that
 *    expands just that group. The cap is measured in real pixels off the
 *    rendered grid (not hardcoded per breakpoint) so it stays correct as
 *    the grid's responsive column count changes. A hidden group's grid
 *    measures 0px tall, so switching to it re-runs applyGridCollapse to
 *    get a real measurement now that it's actually visible. Once a
 *    shopper clicks "View More" on a group it stays expanded even as they
 *    add/remove patches or switch tabs away and back (group.expanded
 *    flag), and re-collapses are only ever computed automatically — never
 *    re-imposed after the shopper has opted to see everything.
 */

(function () {
  'use strict';

  // Patch (slot) size as a % of the image box width, keyed by the pouch's
  // MAX spot count (from its patch:N tag) — bigger pouches with more max
  // spots get proportionally smaller patches so a full row still fits.
  // These are shared DEFAULTS across every product with that max count —
  // e.g. every patch:5 pouch (all "Everyday Pouch" colors) uses the same
  // size unless that specific product overrides it (see patch-size /
  // patch-top tags below). Only touch these if EVERY pouch sharing that
  // max count needs the same adjustment.
  var SLOT_SIZE_BY_MAX = {
    1: 35,
    2: 30,
    3: 25,
    4: 21,
    5: 27
  };

  // Patch PNGs are NOT edge-to-edge letterforms — measured directly off
  // real patch art, each image has roughly 24% transparent padding on
  // both the left and right of the actual glyph (a 200px patch image's
  // visible letter only spans about the middle 51%). Spacing the BOXES
  // edge-to-edge (or even with a small box overlap) still leaves a huge
  // visible gap between the actual letters. ROW_GAP_RATIO overlaps the
  // boxes by roughly (2 * padding) so the glyphs themselves end up close
  // together with a small intentional breathing gap. Gap scales with
  // patch size (not a flat %) since padding is proportional to box size.
  var ROW_GAP_RATIO = -0.42;

  // Vertical center of the patch row, as a % of the image box. Shared
  // DEFAULT across every pouch photo unless a specific product overrides
  // it (see patch-top tag below).
  var ROW_TOP = 54;

  // Max rows shown in the patch grid before it collapses behind a "View
  // More" button (see applyGridCollapse).
  var GRID_ROW_CAP = 3;

  function slotSize(maxCount, override) {
    if (override) return override;
    return SLOT_SIZE_BY_MAX[maxCount] || Math.max(10, 26 - maxCount * 3);
  }

  // Computes the centered, evenly-spaced left/top/size for each currently
  // placed patch. Called fresh every time the count changes so the whole
  // row re-centers ("reflows") around its new width.
  //
  // sizeOverride / topOverride let ONE specific product deviate from the
  // shared defaults above without touching any other product — set via
  // optional "patch-size:NN" / "patch-top:NN" tags on that product (see
  // sections/patch-customizer.liquid). Useful when a pouch's photo has a
  // different shape/framing than the rest of its max-count siblings (e.g.
  // one "Everyday Pouch" color needs a slightly different band than the
  // others, or a photo that isn't cropped quite the same).
  function computeRowPositions(count, maxCount, sizeOverride, topOverride) {
    if (!count) return [];
    var size = slotSize(maxCount, sizeOverride);
    var top = (topOverride || ROW_TOP) + '%';
    var gap = size * ROW_GAP_RATIO;
    var rowWidth = (count * size) + ((count - 1) * gap);
    var startLeft = 50 - (rowWidth / 2);
    var positions = [];

    for (var i = 0; i < count; i++) {
      var left = startLeft + (i * (size + gap)) + (size / 2);
      positions.push({ top: top, left: left + '%', size: size + '%' });
    }

    return positions;
  }

  // cents (integer) -> formatted money string, using the shop's money
  // format template (e.g. "<span class=money>$ {{amount}} CAD</span>").
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var formatString = format || '\${{amount}}';
    return formatString.replace(/\{\{\s*amount\s*\}\}/, value);
  }

  // Small debounce helper for the resize listener (grid row grouping
  // depends on viewport width via the responsive grid-template-columns).
  function debounce(fn, wait) {
    var timeoutId;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function PatchCustomizer(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.moneyFormat = root.dataset.moneyFormat || '\${{amount}}';

    var dataEl = document.getElementById('pc-data-' + this.sectionId);
    this.data = JSON.parse(dataEl.textContent);
    this.pouch = this.data.pouch; // pouch.price is integer cents

    // If the theme's variant picker changes the selected variant (relevant
    // once/if these pouches gain options like size), keep this in sync.
    // Most Shopify 2.0 themes (Dawn and forks) dispatch this event with
    // variant.price already in cents.
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));

    this.pouchImageEl = document.getElementById('pc-pouch-image-' + this.sectionId);
    this.rowEl = document.getElementById('pc-patch-row-' + this.sectionId);
    this.indicatorEl = document.getElementById('pc-slot-indicator-' + this.sectionId);
    this.countLabelEl = document.getElementById('pc-patch-count-' + this.sectionId);
    this.totalPriceEl = document.getElementById('pc-total-price-' + this.sectionId);
    this.noPatchCheckbox = document.getElementById('pc-no-patch-' + this.sectionId);
    this.addToCartBtn = document.getElementById('pc-add-to-cart-' + this.sectionId);
    this.errorEl = document.getElementById('pc-error-' + this.sectionId);

    // The patch picker is split into two groups (Patches for Customization,
    // Letter Patches) switched via toggle tabs so a large, growing catalog
    // stays easy to scan and letters never get mixed in among decorative
    // designs. A group's markup is only rendered in the Liquid if that
    // collection setting is configured, so gridEl/groupEl/tabBtn are null
    // (and skipped) for a group that hasn't been set up yet.
    this.groups = {
      decorative: {
        key: 'decorative',
        groupEl: document.getElementById('pc-patch-group-decorative-' + this.sectionId),
        gridEl: document.getElementById('pc-patch-grid-decorative-' + this.sectionId),
        viewMoreBtn: document.getElementById('pc-patch-viewmore-decorative-' + this.sectionId),
        tabBtn: document.getElementById('pc-patch-tab-decorative-' + this.sectionId),
        expanded: false
      },
      letter: {
        key: 'letter',
        groupEl: document.getElementById('pc-patch-group-letter-' + this.sectionId),
        gridEl: document.getElementById('pc-patch-grid-letter-' + this.sectionId),
        viewMoreBtn: document.getElementById('pc-patch-viewmore-letter-' + this.sectionId),
        tabBtn: document.getElementById('pc-patch-tab-letter-' + this.sectionId),
        expanded: false
      }
    };

    // Matches the default visibility already baked into the Liquid markup
    // (decorative group visible, letter group starts with the `hidden`
    // attribute when both are configured) — see setActiveGroup for how
    // this changes when a shopper clicks a tab.
    this.activeGroupKey = this.groups.decorative.groupEl
      ? 'decorative'
      : (this.groups.letter.groupEl ? 'letter' : null);

    this.maxCount = this.pouch.spotCount;
    this.placedPatches = []; // ordered array of patch objects, length 0..maxCount
    this.customizerId = 'pc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);

    this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));

    Object.keys(this.groups).forEach(function (key) {
      var group = this.groups[key];
      if (group.viewMoreBtn) {
        group.viewMoreBtn.addEventListener('click', this.expandGroup.bind(this, group));
      }
      if (group.tabBtn) {
        group.tabBtn.addEventListener('click', this.setActiveGroup.bind(this, key));
      }
    }, this);

    // Lets a shopper check out with the pouch/bag alone — no patches
    // required — by overriding the "at least one patch" gate below.
    if (this.noPatchCheckbox) {
      this.noPatchCheckbox.addEventListener('change', this.updateAddToCartState.bind(this));
    }

    // <details> hides its content with `display: none` while closed, so
    // row heights measure as 0 if the accordion happens to be collapsed
    // when we first measure. Re-measure the moment it opens.
    var accordionEl = this.root.querySelector('.patch-customizer__accordion');
    if (accordionEl) {
      accordionEl.addEventListener('toggle', function () {
        if (accordionEl.open) this.applyAllGridCollapses();
      }.bind(this));
    }

    // Column count in the responsive grid changes with viewport width, so
    // which patches fall in which row changes too — recompute the collapse
    // height on resize (only while still collapsed; once expanded, stays
    // expanded regardless of viewport).
    window.addEventListener('resize', debounce(function () {
      this.applyAllGridCollapses();
    }.bind(this), 150));

    if (!this.maxCount || this.maxCount < 1) {
      this.showError('No patch spots are configured for this pouch yet (check its patch:N tag).');
      return;
    }

    this.renderSlotIndicator();
    this.renderPatchRow();
    this.renderPatchGrid();
    this.updateCountLabel();
    this.updateTotal();
    this.updateAddToCartState();
  }

  PatchCustomizer.prototype.handleVariantChange = function (event) {
    if (!event.detail || !event.detail.variant) return;
    var variant = event.detail.variant;
    this.pouch.variantId = variant.id;
    this.pouch.price = variant.price; // cents, matching our internal convention
    this.pouch.available = variant.available;
    this.updateTotal();
    this.updateAddToCartState();
  };

  PatchCustomizer.prototype.showError = function (message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  };

  // The numbered strip above the pouch photo. Purely a progress / quick
  // remove control — its circles never move and never sit on top of the
  // product photo. Filled circles can be clicked to remove that patch
  // (by placement order), which re-flows everything after it.
  PatchCustomizer.prototype.renderSlotIndicator = function () {
    var self = this;
    this.indicatorEl.innerHTML = '';

    for (var i = 0; i < this.maxCount; i++) {
      (function (index) {
        var circle = document.createElement('button');
        circle.type = 'button';
        circle.className = 'patch-customizer__slot-circle';
        circle.textContent = index + 1;

        var isFilled = index < self.placedPatches.length;
        var isNext = index === self.placedPatches.length;
        circle.classList.toggle('is-filled', isFilled);
        circle.classList.toggle('is-next', isNext && !isFilled);
        circle.setAttribute('aria-label', isFilled ? 'Remove patch ' + (index + 1) : 'Patch ' + (index + 1) + ' not yet added');
        circle.disabled = !isFilled;

        circle.addEventListener('click', function () {
          if (isFilled) self.removePatchAt(index);
        });

        self.indicatorEl.appendChild(circle);
      })(i);
    }
  };

  // The actual patches, rendered directly on the pouch photo as a
  // horizontally-centered row that re-centers every time the count
  // changes. CSS transitions on left/top/width animate the "bump".
  PatchCustomizer.prototype.renderPatchRow = function () {
    var self = this;
    var positions = computeRowPositions(
      this.placedPatches.length,
      this.maxCount,
      this.pouch.sizeOverride,
      this.pouch.topOverride
    );

    // Reuse existing DOM nodes where possible so the CSS transition has
    // something to animate from (rather than fading in at the new spot).
    var existing = this.rowEl.querySelectorAll('.patch-customizer__patch-row-item');
    while (existing.length > positions.length) {
      this.rowEl.removeChild(existing[existing.length - 1]);
      existing = this.rowEl.querySelectorAll('.patch-customizer__patch-row-item');
    }

    this.placedPatches.forEach(function (patch, index) {
      var item = existing[index];
      if (!item) {
        item = document.createElement('button');
        item.type = 'button';
        item.className = 'patch-customizer__patch-row-item';
        self.rowEl.appendChild(item);
      }

      var pos = positions[index];
      item.style.top = pos.top;
      item.style.left = pos.left;
      item.style.width = pos.size;
      item.style.height = pos.size;
      item.setAttribute('aria-label', 'Remove ' + patch.title);
      item.innerHTML = '<img src="' + patch.image + '" alt="' + patch.title + '">';

      item.onclick = function () {
        self.removePatchAt(index);
      };
    });
  };

  PatchCustomizer.prototype.updateCountLabel = function () {
    if (!this.countLabelEl) return;
    this.countLabelEl.textContent = this.placedPatches.length + ' of ' + this.maxCount + ' added';
  };

  // Renders both patch groups (Patches for Customization, Letter Patches)
  // into their own grid containers, then re-applies each group's
  // independent row cap — even though only the active group's wrapper is
  // visible at a time (see setActiveGroup), both grids are built up front
  // so switching tabs is instant. A group with no gridEl (its collection
  // setting isn't configured, see the Liquid) is skipped entirely.
  PatchCustomizer.prototype.renderPatchGrid = function () {
    var self = this;
    var isFull = this.placedPatches.length >= this.maxCount;

    Object.keys(this.groups).forEach(function (key) {
      var group = self.groups[key];
      if (!group.gridEl) return;

      group.gridEl.innerHTML = '';

      var groupPatches = self.data.patches.filter(function (patch) {
        return patch.group === key;
      });

      groupPatches.forEach(function (patch) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'patch-customizer__patch-card';
        card.disabled = !patch.available || isFull;

        var usedCount = self.placedPatches.filter(function (p) { return p.id === patch.id; }).length;
        card.classList.toggle('is-selected', usedCount > 0);

        var imgHtml = patch.image
          ? '<img src="' + patch.image + '" alt="" loading="lazy">'
          : '<span class="patch-customizer__patch-noimage" aria-hidden="true"></span>';

        card.innerHTML =
          imgHtml +
          (usedCount > 1 ? '<span class="patch-customizer__patch-used-badge">×' + usedCount + '</span>' : '') +
          '<span class="patch-customizer__patch-title">' + patch.title + '</span>' +
          '<span class="patch-customizer__patch-price">' + patch.priceFormatted + '</span>' +
          (patch.available ? '' : '<span class="patch-customizer__sold-out">Sold out</span>');

        card.addEventListener('click', function () {
          self.addPatch(patch);
        });

        group.gridEl.appendChild(card);
      });

      self.applyGridCollapse(group);
    });
  };

  // Caps a single group's grid at GRID_ROW_CAP (3) visible rows, revealing
  // the rest behind that group's own "View More" button. Row boundaries
  // are measured off the actual rendered card positions
  // (getBoundingClientRect) rather than a fixed pixel height, since the
  // number of columns — and therefore which cards land in which row —
  // changes with viewport width and with how many patches are in the
  // group. Each group (letter, decorative) collapses/expands
  // independently of the other.
  PatchCustomizer.prototype.applyGridCollapse = function (group) {
    var grid = group.gridEl;
    var viewMoreBtn = group.viewMoreBtn;
    if (!grid || !viewMoreBtn) return;

    // Reset before measuring so a previous collapse doesn't affect the
    // measurement (a max-height from last time would zero out rect data).
    grid.classList.remove('is-collapsed');
    grid.style.maxHeight = '';
    viewMoreBtn.hidden = true;

    if (group.expanded) return; // shopper already chose to see everything

    var cards = grid.querySelectorAll('.patch-customizer__patch-card');
    if (!cards.length) return;

    var gridRect = grid.getBoundingClientRect();
    if (gridRect.height === 0) return; // accordion/grid not visible yet — nothing to measure

    var rowTops = [];
    cards.forEach(function (card) {
      var top = Math.round(card.getBoundingClientRect().top - gridRect.top);
      if (rowTops.indexOf(top) === -1) rowTops.push(top);
    });
    rowTops.sort(function (a, b) { return a - b; });

    if (rowTops.length <= GRID_ROW_CAP) return; // already fits within the cap

    var cutoffTop = rowTops[GRID_ROW_CAP - 1];
    var maxBottom = 0;
    cards.forEach(function (card) {
      var rect = card.getBoundingClientRect();
      var top = Math.round(rect.top - gridRect.top);
      if (top === cutoffTop) {
        var bottom = top + rect.height;
        if (bottom > maxBottom) maxBottom = bottom;
      }
    });

    grid.style.maxHeight = maxBottom + 'px';
    grid.classList.add('is-collapsed');
    viewMoreBtn.hidden = false;
    viewMoreBtn.textContent = 'View More';
  };

  // Recomputes the collapse state for every group at once (used after
  // resize and after the accordion opens) without rebuilding the cards.
  PatchCustomizer.prototype.applyAllGridCollapses = function () {
    var self = this;
    Object.keys(this.groups).forEach(function (key) {
      var group = self.groups[key];
      if (!group.expanded) self.applyGridCollapse(group);
    });
  };

  PatchCustomizer.prototype.expandGroup = function (group) {
    group.expanded = true;
    if (group.gridEl) {
      group.gridEl.classList.remove('is-collapsed');
      group.gridEl.style.maxHeight = '';
    }
    if (group.viewMoreBtn) group.viewMoreBtn.hidden = true;
  };

  // Switches which group's grid is visible (see the tab buttons in the
  // Liquid). The other group's wrapper just gets `hidden` — its cards stay
  // in the DOM with whatever selections/scroll state they had, nothing is
  // rebuilt. Because a hidden group measures 0px tall, its collapse state
  // may be stale from the last time it was visible (or never computed at
  // all); re-run applyGridCollapse now that it's actually on screen.
  PatchCustomizer.prototype.setActiveGroup = function (key) {
    if (this.activeGroupKey === key || !this.groups[key]) return;
    this.activeGroupKey = key;

    var self = this;
    Object.keys(this.groups).forEach(function (k) {
      var group = self.groups[k];
      var isActive = k === key;
      if (group.groupEl) group.groupEl.hidden = !isActive;
      if (group.tabBtn) {
        group.tabBtn.classList.toggle('is-active', isActive);
        group.tabBtn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
    });

    this.applyGridCollapse(this.groups[key]);
  };

  PatchCustomizer.prototype.addPatch = function (patch) {
    if (this.placedPatches.length >= this.maxCount) {
      this.showError('This pouch fits ' + this.maxCount + ' patch' + (this.maxCount === 1 ? '' : 'es') + ' max — remove one to add a different patch.');
      return;
    }

    this.showError('');
    this.placedPatches.push(patch);
    this.refresh();
  };

  PatchCustomizer.prototype.removePatchAt = function (index) {
    this.showError('');
    this.placedPatches.splice(index, 1);
    this.refresh();
  };

  PatchCustomizer.prototype.refresh = function () {
    this.renderSlotIndicator();
    this.renderPatchRow();
    this.renderPatchGrid();
    this.updateCountLabel();
    this.updateTotal();
    this.updateAddToCartState();
  };

  PatchCustomizer.prototype.updateTotal = function () {
    // pouch.price and patch.price are both integer cents (see comment at
    // top of file) — sum as integers, only convert to a decimal string at
    // the very end via formatMoney.
    var totalCents = this.pouch.price;
    this.placedPatches.forEach(function (patch) {
      totalCents += patch.price;
    });

    this.totalPriceEl.innerHTML = formatMoney(totalCents, this.moneyFormat);
  };

  // Shoppers are not required to fill every spot — a Mini pouch buyer may
  // only want a single letter. Normally at least one patch must be
  // placed, but checking "buy the bag only" (noPatchCheckbox) overrides
  // that gate so the pouch/bag can be added to cart with zero patches.
  PatchCustomizer.prototype.updateAddToCartState = function () {
    var skippingPatches = this.noPatchCheckbox && this.noPatchCheckbox.checked;
    var hasSelection = this.placedPatches.length > 0 || skippingPatches;
    this.addToCartBtn.disabled = !hasSelection || !this.pouch.available;
  };

  PatchCustomizer.prototype.handleAddToCart = function () {
    var self = this;
    var skippingPatches = this.noPatchCheckbox && this.noPatchCheckbox.checked;
    if (!this.placedPatches.length && !skippingPatches) return;

    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = 'Adding...';
    this.showError('');

    var items = [];

    // Staff-facing build sheet: a single, readable line on the POUCH item
    // itself listing every patch in placement order, so whoever is sewing
    // this order doesn't have to hunt down and match separate line items
    // by ID — everything they need is right on the pouch's own line. This
    // property has no underscore, so it also prints on packing slips. If
    // the shopper checked "buy the bag only", make that explicit here too
    // rather than leaving the field blank (which would look like a bug).
    var patchSummary = this.placedPatches.length
      ? this.placedPatches
          .map(function (patch, index) {
            return (index + 1) + ') ' + patch.title;
          })
          .join(',  ')
      : 'No patches — customer chose to buy the bag only';

    items.push({
      id: this.pouch.variantId,
      quantity: 1,
      properties: {
        '_customizer_id': this.customizerId,
        'Pouch Style': this.pouch.title,
        'Patches to Apply (in order)': patchSummary
      }
    });

    this.placedPatches.forEach(function (patch, index) {
      items.push({
        id: patch.variantId,
        quantity: 1,
        properties: {
          '_customizer_id': this.customizerId,
          'Placement': 'Patch ' + (index + 1),
          'Pouch': this.pouch.title
        }
      });
    }, this);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || 'Something went out of stock. Please pick another patch.');
          });
        }
        return response.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('patch-customizer:added', {
          detail: { customizerId: self.customizerId }
        }));
        window.location.href = '/cart';
      })
      .catch(function (err) {
        self.showError(err.message);
        self.addToCartBtn.disabled = false;
        self.addToCartBtn.textContent = 'Add to Cart';
      });
  };

  function init() {
    document.querySelectorAll('.patch-customizer').forEach(function (el) {
      new PatchCustomizer(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
